<template>
  <div
    :class="`high-frequency-game-compliance high-frequency-game-compliance-${variant} flex gap-100 p-100 rounded-m`"
    :data-shaking="animateShaking || undefined"
    @animationend="animateShaking = false"
  >
    <LcIcon
      v-if="disclaimerAcceptedConfig"
      class="icon icon-secondary-secondary"
      name="info"
      size="small"
    />

    <LcCheckbox
      v-else
      no-feedback
      :value="isDisclaimerAccepted"
      @update:value="setDisclaimerAccepted"
    />

    <i18n-t class="text" keypath="highfrequencygame.compliance.text" tag="span">
      <template #cta>
        <button class="text-link" type="button" @click="openModal">
          {{ $t('highfrequencygame.compliance.cta') }}
        </button>
      </template>
    </i18n-t>

    <LazyHighFrequencyGameComplianceModal v-if="isModalVisible" @close="isModalVisible = false" />
  </div>
</template>

<script lang="ts" setup>
type Variant = 'confirmed' | 'default' | 'error'

const { disclaimerAcceptedConfig, disclaimerAcceptedCookie, disclaimerAcceptedError } =
  useHighFrequencyGame()

const isModalVisible = ref(false)
const animateShaking = ref(false)

const isDisclaimerAccepted = computed(() => disclaimerAcceptedCookie.value?.value)

const variant = computed<Variant>(() => {
  if (disclaimerAcceptedConfig.value) {
    return 'default'
  }

  if (disclaimerAcceptedCookie.value?.value) {
    return 'confirmed'
  }

  if (disclaimerAcceptedCookie.value?.unchecked) {
    return 'error'
  }

  return 'default'
})

const setDisclaimerAccepted = (value?: boolean) => {
  if (value === undefined) return

  if (disclaimerAcceptedCookie.value.value && !value) {
    disclaimerAcceptedCookie.value = {
      ...disclaimerAcceptedCookie.value,
      unchecked: true,
      value,
    }
    return
  }

  disclaimerAcceptedCookie.value = { ...disclaimerAcceptedCookie.value, value }
}

const openModal = () => {
  isModalVisible.value = true
}

const isAlreadyAtBottom = () =>
  Math.abs(window.innerHeight + window.scrollY - document.documentElement.scrollHeight) < 1

watch(disclaimerAcceptedError, () => {
  if (!disclaimerAcceptedError.value) return

  disclaimerAcceptedCookie.value = {
    ...disclaimerAcceptedCookie.value,
    unchecked: true,
  }

  if (isAlreadyAtBottom()) {
    animateShaking.value = true
    disclaimerAcceptedError.value = false
    return
  }

  window.scrollTo({
    behavior: 'smooth',
    top: document.documentElement.scrollHeight,
  })

  window.addEventListener('scrollend', () => (animateShaking.value = true), { once: true })

  disclaimerAcceptedError.value = false
})
</script>

<style lang="scss" scoped>
.high-frequency-game-compliance {
  margin-bottom: $main-bottom-navigation-height;
  border: token('size/border/100') solid token('color/border/accent/accent');

  &-error {
    border-color: token('color/border/accent/warning/warning');
    background: token('color/background/accent/warning/warning');
  }

  &-confirmed {
    border-color: token('color/border/accent/success/success');
    background: token('color/background/accent/success/success/light');
  }

  .icon {
    padding-top: 0.125rem;
  }

  .text {
    align-self: center;
  }

  &[data-shaking] {
    animation: shake 400ms ease-in-out;
  }
}

@keyframes shake {
  $range: 0.25rem;

  0%,
  100% {
    transform: translateX(0);
  }

  20% {
    transform: translateX(-$range);
  }

  40% {
    transform: translateX($range);
  }

  60% {
    transform: translateX(-$range);
  }

  80% {
    transform: translateX($range);
  }
}
</style>
